<template>
  <div class="container mt-5">
    <div class="card p-4 shadow border-0" style="border-radius: 16px;">
      <h4 class="mb-4 fw-bold" style="color: #b03a45;">➕ Add New Contract</h4>

      <form @submit.prevent="handleSubmit">
        <div class="row">
          <!-- Reference -->
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label text-secondary">Reference *</label>
              <input v-model="form.reference" type="text" class="form-control border-0" required />
            </div>
          </div>

          <!-- User -->
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label text-secondary">User *</label>
              <select v-model="form.user_id" class="form-select border-0" required>
                <option disabled value="">Select user</option>
                <option v-for="user in users" :key="user.id" :value="user.id">
                  {{ user.firstname }} {{ user.lastname }}
                </option>
              </select>
            </div>
          </div>

          <!-- Contract Type -->
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label text-secondary">Contract Type *</label>
              <select v-model="form.contract_type_id" class="form-select border-0" required>
                <option disabled value="">Select contract type</option>
                <option v-for="type in contractTypes" :key="type.id" :value="type.id">
                  {{ type.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Project -->
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label text-secondary">Project *</label>
              <select v-model="form.project_id" class="form-select border-0" required>
                <option disabled value="">Select project</option>
                <option v-for="project in projects" :key="project.id" :value="project.id">
                  {{ project.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Position -->
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label text-secondary">Position *</label>
              <select v-model="form.position_id" class="form-select border-0" required>
                <option disabled value="">Select position</option>
                <option v-for="position in positions" :key="position.id" :value="position.id">
                  {{ position.english_name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Company -->
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label text-secondary">Company *</label>
              <select v-model="form.company_id" class="form-select border-0" required>
                <option disabled value="">Select company</option>
                <option v-for="company in companies" :key="company.id" :value="company.id">
                  {{ company.english_name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Start Date -->
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label text-secondary">Start Date *</label>
              <input type="date" v-model="form.start_date" class="form-control border-0" required />
            </div>
          </div>

          <!-- End Date -->
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label text-secondary">End Date</label>
              <input type="date" v-model="form.end_date" class="form-control border-0" />
            </div>
          </div>

          <!-- Contract Duration (optional) -->
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label text-secondary">Contract Duration</label>
              <input
                type="text"
                v-model="form.contract_duration"
                class="form-control border-0"
                placeholder="ex , 12 months"
              />
            </div>
          </div>

          <!-- Stage Duration (optional) -->
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label text-secondary">Stage Duration</label>
              <input
                type="text"
                v-model="form.stage_duration"
                class="form-control border-0"
                placeholder="ex , 3 months"
              />
            </div>
          </div>

          <!-- Vacation per Month -->
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label text-secondary">Vacation per Month *</label>
              <input
                type="number"
                v-model="form.vacation_per_month"
                class="form-control border-0"
                step="0.1"
                required
              />
            </div>
          </div>

          <!-- Monthly Salary Brut -->
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label text-secondary">Monthly Salary (Brut) *</label>
              <input
                type="number"
                v-model="form.monthly_salary_brut"
                class="form-control border-0"
                required
              />
            </div>
          </div>

          <!-- Monthly Salary Net -->
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label text-secondary">Monthly Salary (Net) *</label>
              <input
                type="number"
                v-model="form.monthly_salary_net"
                class="form-control border-0"
                required
              />
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="text-end mt-4">
          <button type="submit" class="btn text-white px-4" style="background-color: #b03a45;">
            Save Contract
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import axios from '@/api'

const toast = useToast()
const router = useRouter()

const form = ref({
  reference: '',
  user_id: '',
  contract_type_id: '',
  project_id: '',
  position_id: '',
  company_id: '',
  start_date: '',
  end_date: '',
  contract_duration: '',
  stage_duration: '',
  vacation_per_month: '',
  monthly_salary_brut: '',
  monthly_salary_net: ''
})

const users = ref([])
const contractTypes = ref([])
const projects = ref([])
const companies = ref([])
const positions = ref([])

// ✅ AJOUTE ÇA (important)
const fetchAllUsers = async () => {
  const all = []
  let page = 1
  let lastPage = 1
  const perPage = 100

  do {
    const res = await axios.get('/getAllUser', {
      params: { page, per_page: perPage }
    })

    const data = Array.isArray(res.data?.data) ? res.data.data : []
    all.push(...data)

    lastPage = res.data?.last_page || 1
    page++
  } while (page <= lastPage)

  return all
}

const fetchDependencies = async () => {
  try {
    const [allUsers, typesRes, projectsRes, positionsRes, companiesRes] = await Promise.all([
      fetchAllUsers(),
      axios.get('/contract-types'),
      axios.get('/projects'),
      axios.get('/positions'),
      axios.get('/companies')
    ])

    users.value = allUsers
    contractTypes.value = typesRes.data || []
    projects.value = projectsRes.data || []
    positions.value = positionsRes.data || []
    companies.value = companiesRes.data || []
  } catch (err) {
    console.error(err)
    toast.error('Failed to load dependencies.')
  }
}

const handleSubmit = async () => {
  try {
    await axios.post('/employee/contracts', form.value)
    toast.success('Contract created successfully.')
    router.push('/contracts')
  } catch (err) {
    console.error(err)
    toast.error('Failed to create contract.')
  }
}

onMounted(fetchDependencies)
</script>


<style scoped>
.form-label {
  font-weight: bold;
  color: #b03a45;
}
</style>
